import type { DeadlineBucket, DeadlineSummaryDto } from '@prospero/shared';
import { useEffect, useRef, useState } from 'react';
import { useDeadlineDetail } from './use-deadlines-queries';

export const DEADLINE_DEEP_LINK_PARAM = 'deadlineId';

export function deadlineCardAnchor(deadlineId: string): string {
  return `expected-deadline-${deadlineId}`;
}

/** Stessa regola del pulsante "Carica il documento" della riga: se qui è falso il link solo evidenzia. */
export function canClientUploadDeadline(
  deadline: DeadlineSummaryDto,
  isReadOnly: boolean,
): boolean {
  if (isReadOnly) {
    return false;
  }
  return deadline.managedBy !== 'CONSULTANT' || deadline.clientCanUpload;
}

interface UseDeadlineDeepLinkOptions {
  focusDeadlineId: string | null | undefined;
  /** Righe attese caricate (anche vuote se la sezione è spenta); undefined finché la lista non è pronta. */
  expectedDeadlines: DeadlineSummaryDto[] | undefined;
  isReadOnly: boolean;
  onUpload: (deadline: DeadlineSummaryDto) => void;
  onHandled?: () => void;
}

function scrollToDeadlineCard(deadlineId: string): void {
  document
    .getElementById(deadlineCardAnchor(deadlineId))
    ?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
}

/** Le righe in regola o già in rinnovo con un file vengono solo mostrate: il link non apre il rinnovo. */
const UPLOAD_ON_LINK_BUCKETS: readonly DeadlineBucket[] = ['EXPIRED', 'EXPIRING', 'NO_DEADLINE'];

function needsUploadOnLink(deadline: DeadlineSummaryDto): boolean {
  return (
    UPLOAD_ON_LINK_BUCKETS.includes(deadline.bucket) || deadline.currentSignedDocumentId === null
  );
}

function openUploadWhenAllowed(
  deadline: DeadlineSummaryDto,
  isReadOnly: boolean,
  onUpload: (deadline: DeadlineSummaryDto) => void,
): void {
  if (needsUploadOnLink(deadline) && canClientUploadDeadline(deadline, isReadOnly)) {
    onUpload(deadline);
  }
}

/**
 * Risolve il link `?deadlineId=` degli avvisi. Una riga tra gli attesi caricati viene evidenziata;
 * una fuori pagina, filtrata o già con file si recupera per id. In ogni caso il caricamento o il
 * rinnovo si apre se il cliente può farlo e la riga è scaduta, in scadenza o senza data; le altre
 * vengono solo evidenziate. Riga assente o archiviata: la pagina resta normale.
 */
export function useDeadlineDeepLink({
  focusDeadlineId,
  expectedDeadlines,
  isReadOnly,
  onUpload,
  onHandled,
}: UseDeadlineDeepLinkOptions): string | null {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const handledIdRef = useRef<string | null>(null);
  const isPending =
    Boolean(focusDeadlineId) &&
    expectedDeadlines !== undefined &&
    handledIdRef.current !== focusDeadlineId;
  const listedDeadline = isPending
    ? expectedDeadlines?.find((deadline) => deadline.id === focusDeadlineId)
    : undefined;
  const fetchedDeadline = useDeadlineDetail(
    isPending && !listedDeadline ? (focusDeadlineId ?? null) : null,
  );

  useEffect(() => {
    if (!focusDeadlineId || !isPending) {
      return;
    }
    const target = listedDeadline ?? fetchedDeadline.data;
    if (!target && !fetchedDeadline.isError) {
      return;
    }
    handledIdRef.current = focusDeadlineId;
    if (target && target.archivedAt === null) {
      if (listedDeadline) {
        setHighlightedId(target.id);
        scrollToDeadlineCard(target.id);
      }
      openUploadWhenAllowed(target, isReadOnly, onUpload);
    }
    onHandled?.();
  }, [
    focusDeadlineId,
    isPending,
    listedDeadline,
    fetchedDeadline.data,
    fetchedDeadline.isError,
    isReadOnly,
    onUpload,
    onHandled,
  ]);

  return highlightedId;
}
